import React from 'react';
import { University } from '../../../types';

interface OverviewTabProps {
  university: University;
  onTabChange: (tabId: string) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ university, onTabChange }) => {
  // Format numbers with commas
  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString();
  };

  // Format currency
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `$${num.toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric'
    });
  };

  return (
    <div className="tab-content">
      <h3 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>University Overview</h3>
      
      {/* Top Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-blue-50/50 rounded-xl p-6">
          <h4 className="font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Location</h4>
          <p className="text-2xl font-bold text-blue-600">{university.location}</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{university.environment}</p>
        </div>
        <div className="bg-green-50/50 rounded-xl p-6">
          <h4 className="font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Students</h4>
          <p className="text-3xl font-bold text-green-600">{formatNumber(university.undergrad_students)}</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>undergraduate students</p>
        </div>
      </div>

      {/* About Section */}
      <div className="mb-8">
        <h4 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>About {university.name}</h4>
        <div className="space-y-4 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          <p>
            {university.name} is a prestigious research university located in {university.location}. 
            The campus provides an excellent {university.environment.toLowerCase()} environment for students 
            to pursue their academic goals.
          </p>
          <p>
            With {formatNumber(university.undergrad_students)} undergraduate students and a 
            {university.acceptance_rate ? ` ${university.acceptance_rate}%` : 'n excellent'} acceptance rate, 
            {university.name} offers a competitive yet supportive academic environment for students from diverse backgrounds.
          </p>
        </div>
      </div>

      {/* Bottom Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
          <div className="text-orange-500 text-3xl mb-3">
            <i className="fas fa-calendar"></i>
          </div>
          <h5 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Application Deadline</h5>
          <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>{formatDate(university.application_deadline)}</p>
          <button 
            onClick={() => onTabChange('requirements')}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 rounded-lg font-medium transition-colors"
          >
            Application Requirements
          </button>
        </div>
        <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
          <div className="text-red-500 text-3xl mb-3">
            <i className="fas fa-chart-bar"></i>
          </div>
          <h5 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Acceptance Rate</h5>
          <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>{university.acceptance_rate}%</p>
          <button 
            onClick={() => onTabChange('statistics')}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 rounded-lg font-medium transition-colors"
          >
            See How I Compare
          </button>
        </div>
        <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
          <div className="text-green-500 text-3xl mb-3">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <h5 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Net Price Per Year</h5>
          <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>{formatCurrency(university.net_price_per_year)}</p>
          <button 
            onClick={() => onTabChange('tuition')}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 rounded-lg font-medium transition-colors"
          >
            Calculate Tuition
          </button>
        </div>
      </div>

      {/* Why We Suggested Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 text-xl mr-3 mt-1">
            <i className="fas fa-lightbulb"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 mb-3">Why did we suggest this school for you?</h4>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">1)</span>
                <span>{university.name} matches your request for a prestigious school in a {university.environment.toLowerCase()} environment.</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">2)</span>
                <span>{university.name} has multiple essays that you can write from existing material.</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">3)</span>
                <span>We've detected special opportunities that suggest {university.name} may be actively recruiting applicants this year.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
